import React from "react";
import { Link } from "react-router-dom";
import { TIER_STYLE } from "../data/peptideTiers";

/**
 * Small inline badge that visually classifies content by evidence tier.
 *   Tier 1 (green)  — FDA-approved pharmaceutical
 *   Tier 2 (amber)  — Compounded / off-label use of approved product
 *   Tier 3 (rose)   — Research-only compound
 *
 * Each badge deep-links to the canonical tier definition on /editorial-standards
 * so visitors (and AI search engines) can resolve the meaning in one click.
 *
 * Props
 *   tier   1 | 2 | 3  (required)
 *   size   'sm' | 'md' (default 'sm')
 *   linked boolean — wrap in <Link to="/editorial-standards#tier-N"> (default true)
 *   showFull boolean — render long name ("Tier 1 — FDA-Approved") vs short ("Tier 1")
 *   testid string — optional data-testid suffix (default uses slug)
 */
export const TierBadge = ({ tier, size = "sm", linked = true, showFull = false, testid }) => {
  const style = TIER_STYLE[tier];
  if (!style) return null;

  const padClass = size === "md" ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs";
  const className = `inline-flex items-center gap-1.5 ${padClass} ${style.bgClass} ${style.borderClass} border ${style.textClass} font-semibold rounded-full whitespace-nowrap`;
  const label = showFull ? style.name : style.short;
  const dataTestId = testid ? `tier-badge-${testid}` : `tier-badge-t${tier}`;

  const content = (
    <span className={className} data-testid={dataTestId} title={style.name}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dotClass}`} aria-hidden="true" />
      {label}
    </span>
  );

  if (linked) {
    return (
      <Link to={`/editorial-standards#${style.anchor}`} className="hover:opacity-80 transition-opacity">
        {content}
      </Link>
    );
  }
  return content;
};

export default TierBadge;
